'use client';

import type { CategoryListItemDto } from '@/queries/categories/categories.schema';
import { useTranslations } from 'next-intl';
import CategoryCard from '@/components/entities/categories/CategoryCard';

import { Icon } from '@/components/entities/Sprite/Icon';
import { SliderWrapper } from '@/components/shared/SliderWrapper';
import { AppRoutes } from '@/i18n/constants';
import { Link } from '@/i18n/routing';
import { isProduction } from '@/libs/getEnvironment';

interface MoreCategoriesProps {
  moreCategories: CategoryListItemDto[];
}

const LobbyOtherCategories = ({ moreCategories }: MoreCategoriesProps) => {
  const t = useTranslations('lobby');

  if (moreCategories.length === 0) {
    return null;
  }

  const label = (
    <div className="group flex items-center gap-1">
      <div className="relative flex items-center gap-1 h-6 font-medium text-[1.125rem] leading-5.5 3xl:leading-6">
        <span className="text-white">{t('more-categories')}</span>
        <span
          aria-hidden
          className="max-lg:hidden absolute inset-0 bg-main-front-gradient text-gradient opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-active:opacity-100"
        >
          {t('more-categories')}
        </span>
        <Icon
          name="lobby-title-arrow"
          className="size-4 mt-px lg:mt-0.5  text-white transition-colors duration-200 group-hover:text-c100 group-active:text-c100"
        />
      </div>
    </div>
  );

  return (
    <section className="mb-5.25 3xl:mb-8.25">
      {isProduction()
        ? <div className="inline-flex justify-between items-center mb-2.25 3xl:mb-2">{label}</div>
        : <Link href={AppRoutes.categories} className="inline-flex justify-between items-center mb-2.25 3xl:mb-2">{label}</Link>}

      <div className="-mx-4 relative overflow-x-clip lg:-mx-10 3xl:mr-0">
        <SliderWrapper>
          <div className="flex w-max gap-2.5 lg:gap-3 pl-4 pr-4 lg:pl-10 lg:pr-10 3xl:pr-0">
            {moreCategories.map(category => (
              <div key={category.id} className="w-44.5 h-auto relative shrink-0">
                <div className="absolute inset-0 rounded-[0.75rem] shadow-lg pointer-events-none z-0"></div>
                <CategoryCard category={category} variant="lobby" />
              </div>
            ))}
          </div>
        </SliderWrapper>
        <div
          className="pointer-events-none absolute top-0 right-0 w-23 h-20 z-10 bg-[linear-gradient(270deg,rgba(35,19,51,0.8)_0%,rgba(35,19,51,0.4)_50%,rgba(35,19,51,0)_100%)]"
        />
        <div
          className="pointer-events-none hidden md:block absolute top-0 left-0 w-23 h-20 z-10 bg-[linear-gradient(90deg,rgba(35,19,51,0.8)_0%,rgba(35,19,51,0.4)_50%,rgba(35,19,51,0)_100%)]"
        />
      </div>
    </section>
  );
};

export default LobbyOtherCategories;
